import React, { useCallback } from 'react';
import { Formik, Form, Field, FormikValues } from 'formik';
import { BillingInfoPayload } from 'braintree-web';
import classNames from 'classnames';
import { HtmlString, InputField, withFieldProps, SelectFieldWithLabel } from '@adjust/dashboard-components';

import {
  validate,
  isEmail,
  hasMaxLength,
  onlyAsciiCharacters,
  isRequired,
  ValidationMapper,
} from '@adjust/dashboard-core/lib/validations';
import { User } from '@adjust/dashboard-core/src/api/types';
import { countryOptions } from '@react/constants/countries';
import { CardValidationFields } from '@redux/ducks/paymentMethods';

import { CreditCardField } from './CreditCardField';
import {
  cleanPhoneNumber,
  dropEmptyFields,
  isInvalidField,
  isInvalidForm,
  mapCountryNameToCode,
} from './CreditCardForm.utils';

import './CreditCardForm.scss';

const validation: ValidationMapper = {
  email: [
    [value => value && isEmail(value), () => t('field.invalid-email.error')],
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
  phoneNumber: [
    [hasMaxLength(20), () => t('field.too-long.error')],
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
  givenName: [
    [hasMaxLength(50), () => t('field.too-long.error')],
    [onlyAsciiCharacters, () => t('field.not-ascii.error')],
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
  surname: [
    [hasMaxLength(50), () => t('field.too-long.error')],
    [onlyAsciiCharacters, () => t('field.not-ascii.error')],
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
  streetAddress: [
    [hasMaxLength(50), () => t('field.too-long.error')],
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
  extendedAddress: [[hasMaxLength(50), () => t('field.too-long.error')]],
  locality: [
    [hasMaxLength(50), () => t('field.too-long.error')],
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
  region: [
    [hasMaxLength(50), () => t('field.too-long.error')],
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
  postalCode: [
    [hasMaxLength(10), () => t('field.too-long.error')],
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
  countryCodeAlpha2: [
    [(value, parentValue) => isRequired(parentValue) && isRequired(value), () => t('field.required.error')],
  ],
};

export interface CreditCardFormOwnProps {
  showCancelButton: boolean;
  isDarkTheme?: boolean;
  onSubmit: (payload: BillingInfoPayload) => void;
  onCancel?: () => void;
}

export interface CreditCardFormStateProps {
  user: User;
  validationFields: CardValidationFields;
  cardType: string;
  isError: boolean;
}

type CreditCardFormProps = CreditCardFormOwnProps & CreditCardFormStateProps;

export const CreditCardForm: React.FC<CreditCardFormProps> = ({
  user,
  showCancelButton,
  isDarkTheme,
  validationFields,
  cardType,
  onCancel,
  onSubmit,
  isError,
}) => {
  const submitHandler = useCallback(
    (values: FormikValues) => {
      const { email, ...restFields } = dropEmptyFields(values);
      if (restFields.countryCodeAlpha2) {
        restFields.countryCodeAlpha2 = restFields.countryCodeAlpha2.toUpperCase();
      }

      if (restFields.phoneNumber) {
        restFields.phoneNumber = cleanPhoneNumber(restFields.phoneNumber);
      }

      onSubmit({
        email,
        billingAddress: restFields,
        additionalInformation: {
          workPhoneNumber: restFields?.phoneNumber,
          shippingGivenName: restFields?.givenName,
          shippingSurname: restFields?.surname,
          shippingPhone: restFields?.phoneNumber,
          shippingAddress: {
            streetAddress: restFields?.streetAddress,
            extendedAddress: restFields?.extendedAddress,
            locality: restFields?.locality,
            region: restFields?.region,
            postalCode: restFields?.postalCode,
            countryCodeAlpha2: restFields?.countryCodeAlpha2,
          },
        },
      });
    },
    [onSubmit],
  );

  const handleFormValidate = useCallback((values: FormikValues) => {
    const countryCodeAlpha2Errors = validate(validation.countryCodeAlpha2)(values.countryCodeAlpha2, values.region);
    return {
      ...(countryCodeAlpha2Errors && {
        countryCodeAlpha2: countryCodeAlpha2Errors,
      }),
    };
  }, []);

  const errorClassName = classNames('credit-card__error', {
    'credit-card__error--light': isDarkTheme,
  });
  return (
    <Formik
      initialValues={{
        email: user?.email,
        givenName: user?.last_name,
        surname: user?.first_name,
        countryCodeAlpha2: mapCountryNameToCode(user?.country),
        region: user?.region,
        phoneNumber: null,
        streetAddress: null,
        locality: null,
        postalCode: null,
      }}
      onSubmit={submitHandler}
      validate={handleFormValidate}
    >
      {({ isValid }) => (
        <Form className="flex-one flex-box-column">
          <div className="flex-one flex-box-column scrolling-area">
            {isError && (
              <div className={errorClassName}>
                <p className="info small error with-icon">
                  <i className="icon-warning" />
                  <span>{t('payment-setup.update-error')}</span>
                </p>
              </div>
            )}

            <CreditCardField
              id="card-number"
              label="field.card-number"
              icon={cardType}
              isDarkTheme={isDarkTheme}
              isInvalid={isInvalidField(validationFields, 'number')}
            />

            <CreditCardField
              id="expiration-date"
              label="field.expiration-date"
              isDarkTheme={isDarkTheme}
              isInvalid={isInvalidField(validationFields, 'expirationDate')}
            />

            <CreditCardField
              id="security-code"
              label="field.security-code"
              isDarkTheme={isDarkTheme}
              isInvalid={isInvalidField(validationFields, 'cvv')}
            />

            <div className="credit-card__additional-fields">
              {isError && (
                <div className={errorClassName}>
                  <p className="info small error">
                    <HtmlString>{t('payment-setup.retry-message')}</HtmlString>
                  </p>
                </div>
              )}

              <Field name="email" validate={validate(validation.email)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-email')}
                    placeholder={t('field.billing-email')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="phoneNumber" validate={validate(validation.phoneNumber)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-phone')}
                    placeholder={t('field.billing-phone')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="givenName" validate={validate(validation.givenName)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-first-name')}
                    placeholder={t('field.billing-first-name')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="surname" validate={validate(validation.surname)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-last-name')}
                    placeholder={t('field.billing-last-name')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="streetAddress" validate={validate(validation.streetAddress)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-street-address')}
                    placeholder={t('field.billing-street-address')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="extendedAddress" validate={validate(validation.extendedAddress)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-extended-address')}
                    placeholder={t('field.billing-extended-address')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="locality" validate={validate(validation.locality)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-city')}
                    placeholder={t('field.billing-city')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="postalCode" validate={validate(validation.postalCode)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-postal-code')}
                    placeholder={t('field.billing-postal-code')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="countryCodeAlpha2" validate={validate(validation.countryCodeAlpha2)}>
                {withFieldProps(
                  <SelectFieldWithLabel
                    options={countryOptions}
                    placeholder={t('label.select-country')}
                    label={t('field.billing-country')}
                    menuPlacement="auto"
                    light={isDarkTheme}
                  />,
                )}
              </Field>

              <Field name="region" validate={validate(validation.region)}>
                {withFieldProps(
                  <InputField
                    label={t('field.billing-state')}
                    placeholder={t('field.billing-state')}
                    light={isDarkTheme}
                  />,
                )}
              </Field>
            </div>
          </div>

          <div className="panel-footer flex-auto">
            <div className="flex-box-row">
              {showCancelButton && (
                <button type="button" className="btn flex-one" onClick={onCancel}>
                  {t('action.cancel')}
                </button>
              )}
              <button
                type="submit"
                className="btn btn-main flex-one btn-save-card"
                disabled={isInvalidForm(validationFields) || !isValid}
              >
                {t('action.save')}
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
